export interface Totalizadores {
  pedidosNaoConfirmados: number;
  pcfsNaoProgramadas: number;
  pcfsNaoIniciadas: number;
  pcfsParadas: number;
  pcfsAtrasadas: number;
}

export interface Etapa {
  codigo: number;
  descricao: string;
  quantidade: number;
  atrasadas: number;
  minimoNivel: number;
  maximoNivel: number;
  ordemPainel: number | null;
}

export interface PcfEtapa {
  cliente: string;
  numped: number | null;
  numpcf: number;
  tipo: string;
  dataEntrega: string;
  dataEnvio: string | null;
  posicaoAtual: number;
  situacEtapa: string;
  situacPcf: string;
  descricaoEtapa: string;
  qtdEtapas: number;
  produto: string;
  qtdPec: number;
  pesoTeorico: number;
  qtdMet: number;
  inicioProducao: string | null;
  fimProducao: string | null;
  tempoLimiteMin: number;
  tempoProduzidoMin: number;
  atrasadaData: boolean;
  atrasadaEtapa: boolean;
}

import { getToken, clearToken } from "./auth";

// 403 = sem permissão (perpro) — devolve só a mensagem do servidor para o modal.
async function msg403(r: Response): Promise<string> {
  const j = (await r.json().catch(() => ({}))) as { mensagem?: string; erro?: string; error?: string };
  return j.mensagem ?? j.erro ?? j.error ?? "Usuário sem permissão para este programa.";
}

async function get<T>(url: string): Promise<T> {
  const token = getToken();
  const r = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (r.status === 401) {
    clearToken();
    window.location.reload();
    throw new Error("sessão expirada");
  }
  if (r.status === 403) throw new Error(await msg403(r));
  if (!r.ok) {
    const body = await r.text().catch(() => "");
    throw new Error(`${url}: HTTP ${r.status} — ${body}`);
  }
  return (await r.json()) as T;
}

async function post<T>(url: string, body: unknown): Promise<T> {
  const token = getToken();
  const r = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (r.status === 401) {
    clearToken();
    window.location.reload();
    throw new Error("sessão expirada");
  }
  if (r.status === 403) throw new Error(await msg403(r));
  if (!r.ok) {
    const b = await r.text().catch(() => "");
    throw new Error(`${url}: HTTP ${r.status} — ${b}`);
  }
  return (await r.json()) as T;
}

export const api = {
  totalizadores: (cliente: string | null) =>
    get<Totalizadores>(`/api/totalizadores?cliente=${encodeURIComponent(cliente ?? "")}`),
  etapas: (cliente: string | null) =>
    get<Etapa[]>(`/api/etapas?cliente=${encodeURIComponent(cliente ?? "")}`),
  clientes: () => get<string[]>("/api/clientes"),
  busca: (q: string) =>
    get<BuscaResultado[]>(`/api/busca?q=${encodeURIComponent(q)}`),
  config: () => get<{ refreshDefaultSeconds: number }>("/api/config"),
  pcf: (numpcf: number) => get<PcfDetalhe>(`/api/pcf/${numpcf}`),
};

export interface PcfCabecalho {
  numpcf: number;
  numped: number | null;
  sequen: number | null;
  tipo: string;
  situacPcf: string;
  cliente: string;
  produto: string;
  dataEntrega: string | null;
  dataEnvio: string | null;
  qtdPec: number;
  pesoTeorico: number;
  qtdMet: number;
  comprimento: number;
  frete: number;
}

export interface BuscaResultado {
  numpcf: number;
  numped: number | null;
  cliente: string;
  produto: string;
  situacPcf: string;
}

export interface PcfEtapaProgresso {
  posicao: number;
  codigoEtapa: number;
  descricaoEtapa: string;
  situacEtapa: string;
  tempoLimiteMin: number;
  tempoProduzidoMin: number;
  inicio: string | null;
  fim: string | null;
  eAtual: boolean;
}

export interface PcfHistoricoEvento {
  posicao: number;
  sequen: number;
  descricaoEtapa: string;
  situac: string;
  inicio: string | null;
  fim: string | null;
  operador: string;
  duracaoSeg: number | null;
}

export interface PcfDetalhe {
  cabecalho: PcfCabecalho;
  etapas: PcfEtapaProgresso[];
  historico: PcfHistoricoEvento[];
  posicaoAtual: number;
}

// ===== Apontamento de chão de fábrica (acompanhamento_pcf) =====

export type AcaoApontamento =
  | "INICIAR"
  | "RETOMAR"
  | "FINALIZAR_PAUSA"
  | "REABRIR"
  | "NENHUMA";

export type TipoAcao = "iniciar" | "pausar" | "retomar" | "finalizar" | "pular" | "reabrir";

export interface ApontHistoricoLinha {
  posicao: number;
  etapa: string;
  situac: string;
  operador: string;
  data: string | null;
  hora: string | null;
  tempo: string;
  codmot: number;
  nommot: string;
}

export interface ApontResolver {
  ok: boolean;
  erro?: string;
  abrirEtapasAbertas?: boolean;
  perguntarReabrir?: boolean;
  numpcf: number;
  posicao: number;
  nompro: string;
  codigoEtapa: number;
  nomeEtapa: string;
  situacao: string;
  acao: AcaoApontamento;
  ultimaPosicao: boolean;
  historico: ApontHistoricoLinha[];
  etapasAbertas?: { descri: string; situac: string }[]; // tela-4
  pcfSituac: string; // cadpcf.situac (FINALIZADA = nenhum botão)
  lancarProduto?: boolean; // Inspeção Final sem lotes: o Finalizar abre o lançamento do produto (lancamento_produtos)
  separarMaterial?: boolean; // decisão 24/10/2026: Finalizar da SEPARAÇÃO abre a confirmação/troca do lote
}

// Separação do material da PCF (decisão 24/10/2026)
export interface SeparacaoLinha {
  id: number;
  seqemp: number;
  numlot: number;
  codpro: number;
  nompro: string;
  nomloc: string;
  unidade: string;
  comprimento: string;
  qtdemp: number;
  fisico: number;
}
export interface SeparacaoTroca {
  id: number;
  numlot: number;
  qtd?: number;
}

export interface ApontOperador {
  ok: boolean;
  erro?: string;
  operador?: string;
}

export interface ApontMotivo {
  codmot: number;
  nommot: string;
}

export interface ApontAcaoResultado {
  ok: boolean;
  erro?: string;
  operador?: string;
  situacaoPcf?: string;
  precisaEncarregado?: boolean; // decisão 25/10/2026: a finalização baixa material — pedir o encarregado (reunião 28/10/2026: token)
}

export interface RoteiroLinha {
  posicao: number;
  etapa: number;
  descricao: string;
  dimensionais: string;
  caracteristicas: string;
  tempoEtapa: number;
}

export interface EtapaDisponivel {
  codigo: number;
  descri: string;
  movimenta?: boolean; // exige matéria-prima (lote)
}

export interface LoteInfo {
  numlot: number;
  nommat: string;
  nomloc: string;
  comprimento: string;
  saldoPeso: number;
  saldoPecas: number;
  unidade: string;
  ehKg: boolean;
  qtdEmpenho?: number;
  erro?: string;
}

export interface EmpenhoLinha {
  numlot: number;
  nomloc: string;
  nompro: string;
  unidade: string;
  comprimento: string;
  qtdemp: number;
  seqemp: number;
}

export interface LoteBusca {
  numlot: number;
  nomloc: string;
  nompro: string;
  unidade: string;
  largura: number;
  espessura: number;
  comprimento: string;
  diametro: number;
  saldoPeso: number;
  saldoPecas: number;
}

// ===== Painel de configuração da conexão SQL (setup) =====

export interface SetupSqlInput {
  server: string;
  port?: number | null;
  database: string;
  user: string;
  password: string;
  options?: { trustServerCertificate?: boolean; encrypt?: boolean };
}

export interface SetupStatus {
  configurado: boolean;
  conectado: boolean;
  erro?: string;
  sql: {
    server: string;
    port: number | null;
    database: string;
    user: string;
    temSenha: boolean;
    options?: { trustServerCertificate?: boolean; encrypt?: boolean };
  };
}

export const apiSetup = {
  status: () => get<SetupStatus>("/api/setup/status"),
  testar: (s: SetupSqlInput) =>
    post<{ ok: boolean; erro?: string; agora?: string }>("/api/setup/testar", s),
  salvar: (s: SetupSqlInput) =>
    post<{ ok: boolean; erro?: string; conectado?: boolean }>("/api/setup/salvar", s),
};

// ===== Módulo PCF — TELA-1 "Planejamento de Produção" (atcadpcf) =====

export interface PcpPeriodo {
  ini: string | null; // YYYY-MM-DD
  fim: string | null;
  nomeBd?: string; // cadpar.nome_bd (trava ACOSNOGUEIRA)
  erpUrl?: string; // ERP web — abre o pedido (CALLFORM ATCADPED)
}

export interface PcpLinha {
  cliente: string;
  numped: number;
  produto: string;
  qtd: number;
  dataEntrega: string; // dd/mm/yyyy
  codcli: number;
  codpro: number;
  sequen: number;
  numpcf?: number; // PCF já ligada ao item (faturamento antecipado) — Produzir abre esta PCF
}

// TELA-PCF "Plano de Controle de Fabricação" — formulário (carregar/novo).
export interface PcfForm {
  numpcf: number; // 0 = registro novo
  tipo: string; // VENDA | DEMANDA | COMPLEMENTAR
  numped: number;
  datemi: string | null; // YYYY-MM-DD
  datent: string | null;
  codusu: number;
  nomusu: string;
  codcli: number;
  nomcli: string;
  codven: number;
  nomven: string;
  codpro: number;
  nompro: string;
  sequen: number;
  situac: string;
  numeop: number;
  numlot: number;
  corrida: string;
  nfEntrada: string;
  diaext: number;
  espessura: number;
  gaveteiro: string;
  qtdpec: number;
  qtdmet: number;
  peso: number;
  nomaca: string;
  comprimentoFixo: number;
  comprimentoOriginal: number;
  compmin: number;
  compmax: number;
  tolextMin: number;
  tolextMax: number;
  tolintMin: number;
  tolintMax: number;
  tolespMin: number;
  tolespMax: number;
  observ: string;
  codastm: number;
  nomastm: string;
  frete: string;
  nroite: string;
  tipoPcf: string;
  numPc: string;
  certificado: boolean;
  urgente: boolean;
  etiquetar: boolean;
  opcoes: string;
  codfor: number;
  nomfor: string;
  nomeBd: string;
}

export interface CombosPcf {
  vendedores: string[];
  normas: string[];
  acabamentos: string[];
  fretes: string[];
  embalagens: string[];
  tiposPcf: string[];
}

export interface TolerResultado {
  compmin: number;
  compmax: number;
  tolextMin: number;
  tolextMax: number;
  tolespMin: number;
  tolespMax: number;
  codastm: number;
}

export interface FornecedorResultado {
  codfor: number;
  nomfor: string;
  erro?: string;
}

export interface PcfImpEtapa {
  posicao: number;
  etapa: string;
  caracteristicas: string;
  dimensionais: string;
  codbar: string;
}
export interface PcfImpHeader {
  numpcf: number;
  tipo: string;
  numped: number | null;
  nroite: string;
  datent: string | null;
  datemi: string | null;
  nomusu: string;
  codcli: number | null;
  nomven: string;
  nompro: string;
  liga: string;
  costura: string;
  qtdpec: number;
  compmin: number;
  compmax: number;
  tolextMin: number;
  tolextMax: number;
  tolintMin: number;
  tolintMax: number;
  tolespMin: number;
  tolespMax: number;
  observ: string | null;
  situac: string;
  opcoes: string;
  certificado: string;
  numeop: string | null;
  numlot: string | null;
  corrida: string;
  nfEntrada: string;
  gaveteiro: string;
  tipser: string;
  pesoTotal: number;
  metragemTotal: number;
  frete: string;
  acabamento: string;
  bitola: string | null;
  nomeAstm: string;
  dataImpressao: string;
  tipoPcf: string;
  numPc: string;
  urgente: string;
  etiquetar: string;
  codfor: number | null;
  comprimentoFixo: number;
}
export interface PcfImpressao {
  header: PcfImpHeader | null;
  etapas: PcfImpEtapa[];
}

// Ações de ciclo de vida: 200 -> {ok}; 400 -> {ok:false, mensagem}.
export type LifecycleResp = { ok: true } | { ok: false; mensagem: string };

async function postLifecycle(url: string, body: unknown): Promise<LifecycleResp> {
  const token = getToken();
  const r = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  if (r.status === 401) {
    clearToken();
    window.location.reload();
    throw new Error("sessão expirada");
  }
  if (r.ok) return { ok: true };
  if (r.status === 403) return { ok: false, mensagem: await msg403(r) };
  if (r.status === 400) {
    const j = (await r.json().catch(() => ({}))) as { mensagem?: string };
    return { ok: false, mensagem: j.mensagem ?? "Operação não permitida." };
  }
  const b = await r.text().catch(() => "");
  throw new Error(`${url}: HTTP ${r.status} — ${b}`);
}

// Gravação: 200 -> {ok,numpcf}; 400 -> {ok:false, campo, mensagem} (consistências).
export type GravacaoResp =
  | { ok: true; numpcf: number }
  | { ok: false; campo: string; mensagem: string };

async function postGravacao(url: string, body: unknown): Promise<GravacaoResp> {
  const token = getToken();
  const r = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  if (r.status === 401) {
    clearToken();
    window.location.reload();
    throw new Error("sessão expirada");
  }
  if (r.ok) {
    const j = (await r.json()) as { numpcf: number };
    return { ok: true, numpcf: j.numpcf };
  }
  if (r.status === 400) {
    const j = (await r.json().catch(() => ({}))) as { campo?: string; mensagem?: string };
    return { ok: false, campo: j.campo ?? "", mensagem: j.mensagem ?? "Falha de consistência." };
  }
  const b = await r.text().catch(() => "");
  throw new Error(`${url}: HTTP ${r.status} — ${b}`);
}

// Faturamento antecipado (24/10/2026): alerta do PCP + trilha do pedido (server/src/queries/fat-antecipado.ts).
export interface AntecipadoPcf { numpcf: number; sequen: number; situac: string; nompro: string }
export interface AntecipadoLinha {
  numped: number; nomcli: string; situac: string; numenf: number;
  datfat: string; diasFat: number | null; datent: string; diasEntrega: number | null; pcfs: AntecipadoPcf[];
}
export interface TrilhaEtapa { k: string; t: string; feito: boolean; data: string; info?: string }
export interface TrilhaPedido { ok: boolean; erro?: string; numped?: number; situac?: string; fatAntecipado?: boolean; etapas?: TrilhaEtapa[] }

export const apiPcf = {
  periodo: () => get<PcpPeriodo>("/api/pcf/planejamento/periodo"),
  antecipados: () => get<{ disponivel: boolean; itens: AntecipadoLinha[]; erpUrl?: string }>("/api/pcf/antecipados"),
  trilha: (numped: number) => get<TrilhaPedido>(`/api/pcf/pedido-trilha?numped=${numped}`),
  clientes: (ini: string, fim: string) =>
    get<string[]>(`/api/pcf/planejamento/clientes?ini=${ini}&fim=${fim}`),
  produtos: (ini: string, fim: string, cliente: string | null) =>
    get<string[]>(
      `/api/pcf/planejamento/produtos?ini=${ini}&fim=${fim}&cliente=${encodeURIComponent(
        cliente ?? ""
      )}`
    ),
  listar: (ini: string, fim: string, cliente: string | null, produto: string | null) =>
    get<PcpLinha[]>(
      `/api/pcf/planejamento?ini=${ini}&fim=${fim}&cliente=${encodeURIComponent(
        cliente ?? ""
      )}&produto=${encodeURIComponent(produto ?? "")}`
    ),
  retirar: (numped: number, sequen: number) =>
    post<{ removidos: number }>("/api/pcf/planejamento/retirar", { numped, sequen }),
  // Formulário TELA-PCF
  combos: () => get<CombosPcf>("/api/pcf/combos"),
  carregar: (numpcf: number) => get<PcfForm>(`/api/pcf/form/${numpcf}`),
  nova: (p: {
    tipo: string;
    numped: number;
    sequen: number;
    codcli: number;
    codpro: number;
    qtd?: number;
    comprimento?: number;
    datent?: string | null;
  }) => {
    const q = new URLSearchParams();
    q.set("tipo", p.tipo);
    q.set("numped", String(p.numped));
    q.set("sequen", String(p.sequen));
    q.set("codcli", String(p.codcli));
    q.set("codpro", String(p.codpro));
    if (p.qtd != null) q.set("qtd", String(p.qtd));
    if (p.comprimento != null) q.set("comprimento", String(p.comprimento));
    if (p.datent) q.set("datent", p.datent);
    return get<PcfForm>(`/api/pcf/nova?${q.toString()}`);
  },
  incluir: (f: PcfForm) => postGravacao("/api/pcf/incluir", f),
  alterar: (f: PcfForm) => postGravacao("/api/pcf/alterar", f),
  tolerancias: (p: {
    nomastm: string;
    codastm?: number;
    diaext: number;
    espessura: number;
    compmin: number;
    compmax: number;
    tolextMin: number;
    tolextMax: number;
    tolespMin: number;
    tolespMax: number;
  }) => {
    const q = new URLSearchParams();
    q.set("nomastm", p.nomastm);
    q.set("codastm", String(p.codastm ?? 0));
    q.set("diaext", String(p.diaext));
    q.set("espessura", String(p.espessura));
    q.set("compmin", String(p.compmin));
    q.set("compmax", String(p.compmax));
    q.set("tolextMin", String(p.tolextMin));
    q.set("tolextMax", String(p.tolextMax));
    q.set("tolespMin", String(p.tolespMin));
    q.set("tolespMax", String(p.tolespMax));
    return get<TolerResultado>(`/api/pcf/tolerancias?${q.toString()}`);
  },
  fornecedor: (codfor: number) =>
    get<FornecedorResultado>(`/api/pcf/fornecedor?codfor=${codfor}`),
  impressao: (numpcf: number) => get<PcfImpressao>(`/api/pcf/${numpcf}/impressao`),
  finalizar: (numpcf: number) => postLifecycle("/api/pcf/finalizar", { numpcf }),
  cancelar: (numpcf: number) => postLifecycle("/api/pcf/cancelar", { numpcf }),
  // Criar PCF (Demanda / Complementar)
  buscarCliente: (q: string) => get<OpcaoCliente[]>(`/api/pcf/buscar-cliente?q=${encodeURIComponent(q)}`),
  buscarFornecedor: (q: string) => get<OpcaoCliente[]>(`/api/pcf/buscar-fornecedor?q=${encodeURIComponent(q)}`),
  buscarProduto: (q: string) => get<OpcaoProduto[]>(`/api/pcf/buscar-produto?q=${encodeURIComponent(q)}`),
  produtoDados: (codpro: number) => get<ProdutoDados>(`/api/pcf/produto-dados?codpro=${codpro}`),
  pedidoComplementar: (numped: number) =>
    get<PedidoComplementar>(`/api/pcf/pedido-complementar?numped=${numped}`),
  avisos: (numped: number, codpro: number) =>
    get<AvisoPcf[]>(`/api/pcf/avisos?numped=${numped}&codpro=${codpro}`),
  // PCF_ETAPAS (roteiro + matéria-prima)
  roteiro: (numpcf: number) => get<RoteiroLinha[]>(`/api/pcf/roteiro?numpcf=${numpcf}`),
  etapas: () => get<EtapaDisponivel[]>("/api/pcf/etapas"),
  lote: (numlot: number, validar = true) =>
    get<LoteInfo>(`/api/pcf/lote?numlot=${numlot}${validar ? "" : "&validar=0"}`),
  roteiroLote: (numpcf: number, posicao: number, etapa: number) =>
    get<{ numlot: number; qtd: number } | null>(
      `/api/pcf/roteiro-lote?numpcf=${numpcf}&posicao=${posicao}&etapa=${etapa}`
    ),
  roteiroIncluir: (p: {
    numpcf: number;
    etapaCodigo: number;
    dimensionais: string;
    caracteristicas: string;
    tempoEtapa: number;
    numlot?: number;
    qtdSelecionada?: number;
  }) => postLifecycle("/api/pcf/roteiro/incluir", p),
  roteiroAlterar: (p: {
    numpcf: number;
    posicao: number;
    etapaCodigo: number;
    dimensionais: string;
    caracteristicas: string;
    tempoEtapa: number;
    numlot?: number;
    qtdSelecionada?: number;
  }) => postLifecycle("/api/pcf/roteiro/alterar", p),
  roteiroExcluir: (numpcf: number, posicao: number, etapa: number) =>
    postLifecycle("/api/pcf/roteiro/excluir", { numpcf, posicao, etapa }),
  roteiroReordenar: (numpcf: number, ordem: number[]) =>
    postLifecycle("/api/pcf/roteiro/reordenar", { numpcf, ordem }),
  roteiroMover: (numpcf: number, posicao: number, direcao: "subir" | "descer") =>
    postLifecycle("/api/pcf/roteiro/mover", { numpcf, posicao, direcao }),
  roteiroNovaPosicao: (numpcf: number, posicao: number, nova: number) =>
    postLifecycle("/api/pcf/roteiro/nova-posicao", { numpcf, posicao, nova }),
  lotes: (f: { largura: string; espessura: string; diametro: string; comprimento: string }) =>
    get<LoteBusca[]>(`/api/pcf/lotes?${new URLSearchParams(f).toString()}`),
  empenhos: (numpcf: number) => get<EmpenhoLinha[]>(`/api/pcf/empenhos?numpcf=${numpcf}`),
  empenho: (numpcf: number, numlot: number, seqemp: number) =>
    get<LoteInfo>(`/api/pcf/empenho?numpcf=${numpcf}&numlot=${numlot}&seqemp=${seqemp}`),
  // Duplicação
  duplicarCombos: () => get<CombosDuplicar>("/api/pcf/duplicar/combos"),
  duplicarProdutoSpecs: (codpro: number) =>
    get<ProdutoSpecs>(`/api/pcf/duplicar/produto-specs?codpro=${codpro}`),
  buscarDuplicar: (p: {
    numpcf?: number;
    nomcli?: string;
    codcli?: number;
    codpro?: number;
    porMedidas?: boolean;
    tipmat?: string;
    matpri?: string;
    aisi?: string;
    costura?: string;
    astm?: string;
    largMin?: number;
    largMax?: number;
    espMin?: number;
    espMax?: number;
    diamMin?: number;
    diamMax?: number;
  }) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(p)) {
      if (k === "nomcli" && v != null) {
        q.set(k, String(v)); // vazio = todos (LIKE '%%')
        continue;
      }
      if (v == null || v === "" || v === false) continue;
      q.set(k, v === true ? "1" : String(v));
    }
    return get<PcfBuscaLinha[]>(`/api/pcf/duplicar/buscar?${q.toString()}`);
  },
  duplicar: (numpcf: number, cliPro?: { numped: number }) =>
    postGravacao("/api/pcf/duplicar", cliPro ? { numpcf, cliPro: true, numped: cliPro.numped } : { numpcf }),
  duplicarClientes: () => get<{ codigo: number; nomfan: string }[]>("/api/pcf/duplicar/clientes"),
  // tela-duplicacao (Duplicar da PCF nova): copia tolerâncias e etapas de uma PCF antiga.
  duplicarDeCliente: (numpcf: number) =>
    get<{ nomcli: string; erro?: string }>(`/api/pcf/duplicar-de/cliente?numpcf=${numpcf}`),
  duplicarDe: (numpcf: number, numpcfOld: number) => postLifecycle("/api/pcf/duplicar-de", { numpcf, numpcfOld }),
  // botao-imprimir: põe a PCF em produção antes de imprimir.
  imprimir: (numpcf: number) => postLifecycle("/api/pcf/imprimir", { numpcf }),
};

export interface CombosDuplicar {
  astm: string[];
  aisi: string[];
  matpri: string[];
  tipmat: string[];
  costuras: string[];
}
export interface ProdutoSpecs {
  codpro: number;
  nompro: string;
  tipmat: string;
  matpri: string;
  aisi: string;
  costura: string;
  astm: string;
}

export interface PcfBuscaLinha {
  numpcf: number;
  nomcli: string;
  nompro: string;
  datemi: string | null;
  qtdpec: number;
  compmin: number;
  compmax: number;
  qtdmet: number;
  pesoTeorico: number;
}

export interface OpcaoCliente {
  codigo: number;
  nome: string;
}
export interface OpcaoProduto {
  codpro: number;
  nompro: string;
}
export interface ProdutoDados {
  codpro: number;
  nompro: string;
  diaext: number;
  espessura: number;
  pesoTeoricoMetro: number;
}
export interface ItemPedido {
  sequen: number;
  codpro: number;
  nompro: string;
  qtdped: number;
  comprimento: number;
  valliq: number;
  totite: number;
}
export interface PedidoComplementar {
  codcli: number;
  nomcli: string;
  itens: ItemPedido[];
  erro?: string;
}
export interface AvisoPcf {
  numpcf: number;
  nomcli: string;
  qtdpec: number;
  datent: string | null;
}

export const apiApont = {
  pcfDetalhe: (numpcf: number) => get<PcfDetalhe>(`/api/apontamento/pcf/${numpcf}`),
  resolver: (codbar: number) =>
    post<ApontResolver>("/api/apontamento/resolver", { codbar }),
  operador: (senha: string) =>
    post<ApontOperador>("/api/apontamento/operador", { senha }),
  motivos: () => get<ApontMotivo[]>("/api/apontamento/motivos"),
  acao: (
    acao: TipoAcao,
    params: { numpcf: number; posicao: number; senha: string; codmot?: number; lancamento?: LancamentoProduto; separacao?: { trocas: SeparacaoTroca[] }; encarregado?: { codusu: number; token: string } }
  ) => post<ApontAcaoResultado>(`/api/apontamento/${acao}`, params),
  separacao: (numpcf: number) =>
    get<{ ok: boolean; erro?: string; nompro: string; linhas: SeparacaoLinha[]; confirmada: boolean }>(`/api/apontamento/separacao/${numpcf}`),
  separacaoAlternativas: (numlot: number) =>
    get<{ ok: boolean; erro?: string; lotes: { numlot: number; nomloc: string; unidade: string; comprimento: string; livre: number }[] }>(
      `/api/apontamento/separacao/lote/${numlot}/alternativas`
    ),
  // lancamento_produtos / local_estoque (acompanhamento_pcf, legado novo)
  lancamentoDados: (numpcf: number) =>
    get<{ ok: boolean; nompro?: string; numped?: number; unidped?: string; locais: { codloc: number; nomloc: string }[]; sucataPontas?: SucataPontasOpcoes }>(`/api/apontamento/lancamento/${numpcf}`),
  // reunião 28/10/2026: encarregado + token temporário (celular) no lugar da senha; devolve a autorização de uso único
  localEstoque: (codloc: number, codusu: number, token: string) =>
    post<{ ok: boolean; erro?: string; campo?: string; nomloc?: string; nomusu?: string; autorizacao?: string }>("/api/apontamento/local-estoque", { codloc, codusu, token }),
  encarregados: () => get<{ itens: { codusu: number; nomusu: string }[] }>("/api/apontamento/encarregados"),
  // Roteiro de etapas (PCF_ETAPAS)
  roteiro: (numpcf: number) => get<RoteiroLinha[]>(`/api/apontamento/roteiro/${numpcf}`),
  etapasDisponiveis: () => get<EtapaDisponivel[]>("/api/apontamento/etapas-disponiveis"),
  roteiroIncluir: (p: {
    numpcf: number;
    etapaCodigo: number;
    dimensionais: string;
    caracteristicas: string;
    tempoEtapa: number;
    senha: string;
  }) => post<{ ok: boolean; erro?: string; posicao?: number }>("/api/apontamento/roteiro/incluir", p),
  roteiroMover: (numpcf: number, posicao: number, direcao: "subir" | "descer", senha: string) =>
    post<{ ok: boolean; erro?: string }>("/api/apontamento/roteiro/mover", { numpcf, posicao, direcao, senha }),
  // pcf_etapas botao-imprimir: r1cadpcf (rota pública da estação)
  impressao: (numpcf: number) => get<PcfImpressao>(`/api/apontamento/impressao/${numpcf}`),
};

// ===== Produção: Fila, Finalização manual, Reverter cancelamento =====
export interface FilaLinha {
  numpcf: number;
  nompro: string;
  qtdpec: number;
  datent: string | null;
  numped: number;
  observ: string;
  posicao: number;
}
export interface PcfResumo {
  numpcf: number;
  nomcli: string;
  tipo: string;
  numped: number;
  situac: string;
  etapas: { posicao: number; descri: string; dimensionais: string; caracteristicas: string; tempoEtapa: number }[];
  erro?: string;
}
// ===== Estoque: C1LOGEST / C2LOGEST / ATCADACE =====
export interface MovimentoLinha {
  data: string;
  hora: string;
  numlot?: number; // c1logest novo
  unidade?: string; // c1logest novo
  tipo?: "MP" | "PF";
  historico: string;
  pesoAnterior: number;
  pesoMovimentado: number;
  pesoAtual: number;
  usuario: string;
  saida: boolean;
  local: string;
}
export interface HistoricoResp {
  ok: boolean;
  erro?: string;
  nompro?: string;
  nomcli?: string;
  linhas?: MovimentoLinha[];
  truncado?: boolean;
}
export interface LoteAcerto {
  numlot: number;
  nompro: string;
  nomloc: string;
  codloc: number;
  compIni: number;
  compFim: number;
  unidade: string;
  ehKg: boolean;
  corrida: string;
  saldo: number;
  erro?: string;
}
// Programas (perpro) bloqueados para o usuário logado — só para esconder telas/botões (a trava é no servidor).
export type ProgramaPerm = "ATCADACE" | "ATCADPCF" | "PCF_CONFIRMACAO" | "PCF_FINALIZACAO";
export const apiPermissoes = () => get<{ bloqueados: ProgramaPerm[] }>("/api/me/permissoes");

export const apiEstoque = {
  loteDescricao: (numlot: number) => get<{ nompro: string }>(`/api/estoque/lote/${numlot}/descricao`),
  lotesBusca: (q: string) =>
    get<{ numlot: number; codpro: number; nompro: string; unidade: string; nomloc: string }[]>(`/api/estoque/lotes/busca?q=${encodeURIComponent(q)}`),
  historicoLote: (numlot: number) => get<HistoricoResp>(`/api/estoque/lote/${numlot}/historico`),
  clientePcf: (numpcf: number) => get<{ ok: boolean; erro?: string; nomcli?: string }>(`/api/estoque/pcf/${numpcf}/cliente`),
  historicoPcf: (numpcf: number) => get<HistoricoResp>(`/api/estoque/pcf/${numpcf}/historico`),
  aceLote: (numlot: number) => get<LoteAcerto>(`/api/estoque/acerto/lote/${numlot}`),
  aceGravar: (p: { numlot: number; corrida: string; qtdace: number; tipo: "E" | "S"; historico: string }) =>
    postLifecycle("/api/estoque/acerto", p),
};

export const apiProducao = {
  fila: (recompactar = false) => get<FilaLinha[]>(`/api/producao/fila${recompactar ? "?recompactar=1" : ""}`),
  filaMover: (numpcf: number, direcao: "subir" | "descer") => postLifecycle("/api/producao/fila/mover", { numpcf, direcao }),
  filaPosicao: (numpcf: number, posicao: number) => postLifecycle("/api/producao/fila/posicao", { numpcf, posicao }),
  filaDatent: (numpcf: number, datent: string) => postLifecycle("/api/producao/fila/datent", { numpcf, datent }),
  filaFinalizar: (numpcfs: number[], motivo: string) => postLifecycle("/api/producao/fila/finalizar", { numpcfs, motivo }),
  manualConsultar: (numpcf: number) => get<PcfResumo>(`/api/producao/finalizacao-manual?numpcf=${numpcf}`),
  manualFinalizar: (numpcf: number, motivo: string) => postLifecycle("/api/producao/finalizacao-manual", { numpcf, motivo }),
  reverterConsultar: (numpcf: number) => get<PcfResumo>(`/api/producao/reverter-cancelamento?numpcf=${numpcf}`),
  reverter: (numpcf: number) => postLifecycle("/api/producao/reverter-cancelamento", { numpcf }),
  // altera_tempo_etapa
  alteraTempoConsultar: (numpcf: number) => get<AlteraTempoPcf>(`/api/producao/altera-tempo-etapa?numpcf=${numpcf}`),
  alteraTempoGravar: (p: { numpcf: number; posicao: number; etapa: string; tempo: number }) =>
    postLifecycle("/api/producao/altera-tempo-etapa", p),
  // reabre_pcf
  reabreConsultar: (numpcf: number) => get<PcfResumo>(`/api/producao/reabre-pcf?numpcf=${numpcf}`),
  reabrir: (numpcf: number) => postLifecycle("/api/producao/reabre-pcf", { numpcf }),
};
export interface AlteraTempoPcf {
  ok: boolean;
  erro?: string;
  nomcli?: string;
  tipo?: string;
  numped?: number;
  etapas?: PcfResumo["etapas"];
}

// ===== Produção: Consulta de Produção (consulta_producao) =====
export interface ConsultaLinha {
  nomcli: string;
  numped: number;
  tipo: string;
  datent: string;
  datenv: string;
  numpcf: number;
  situacTela: string;
  inicio: string;
  fim: string;
  tempo: string;
  tempoMax: string;
  posicao: number | null;
  qtdPosicoes: number;
  situacPcf: string;
  status: "" | "ATRASO_DATA" | "ATRASO_ETAPA" | "ATRASO_DATA_ETAPA";
  nompro: string;
  qtdpec: number;
  pesoTeorico: number;
  qtdmet: number;
  frete: string;
  proximaEtapa: string;
}
export interface ConsultaFiltros {
  parametro: string;
  filtro: "" | "Data de Entrega" | "Faixa de PCFs";
  numpcfIni: number;
  numpcfFim: number;
  datentIni: string;
  datentFim: string;
  numped: number;
  nomcli: string;
  finalizadas: boolean;
  canceladas: boolean;
  atrasadas: boolean;
  etaabePcfFin: boolean;
  etarec: boolean;
  ocultarAbertas: boolean;
}
export interface DetalheApontamento {
  processo: string;
  situac: string;
  operador: string;
  data: string;
  hora: string;
  tempo: string;
  posicao: number | null;
  sequen: number | null;
  etapa: number;
}
export interface DetalhePcf {
  numpcf: number;
  posicao: number;
  etapa: string;
  nompro: string;
  operador: string;
  situac: string;
  situacPcf: string;
  linhas: DetalheApontamento[];
  podeAlterar: boolean;
}
export const apiConsulta = {
  clientes: () => get<string[]>("/api/producao/consulta/clientes"),
  consultar: (f: ConsultaFiltros) => post<{ linhas: ConsultaLinha[]; excedeu: boolean }>("/api/producao/consulta", f),
  detalhe: (numpcf: number) => get<DetalhePcf>(`/api/producao/consulta/detalhe?numpcf=${numpcf}`),
  excluirApontamento: (p: { numpcf: number; posicao: number; sequen: number; etapa: number; situac: string }) =>
    postLifecycle("/api/producao/consulta/excluir-apontamento", p),
};

// ===== callpedido (card "Pedidos não confirmados") =====
export interface PedidoLinha {
  nomemp: string;
  numped: number;
  nomcli: string;
  datemi: string;
  datent: string;
  valtot: number;
  situac: string;
}
export const apiPedidos = {
  empresas: () => get<string[]>("/api/producao/pedidos/empresas"),
  listar: (f: { nomemp: string; nomcli: string; pedcli: string; situac: string; tipoData: string; datini: string }) =>
    post<{ linhas: PedidoLinha[]; total: number }>("/api/producao/pedidos", f),
};

// ===== painel_producao_tv (sem login) =====
export interface TvLinha {
  ordem: number;
  numpcf: number;
  etapa: string;
  situac: string;
  restante: string;
  cor: "branco" | "verde" | "amarelo" | "vermelho" | "preto";
}
export interface TvDados {
  naoIniciadas: number;
  paradas: number;
  pedidos: number;
  naoProgramadas: number;
  atrasadas: number;
  linhas: TvLinha[];
}
export const apiTv = {
  dados: async (): Promise<TvDados> => {
    const r = await fetch("/api/tv");
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return (await r.json()) as TvDados;
  },
};


// ===================================================================== PCF × estoque (legado novo, 22/10/2026)
export interface LancamentoProduto {
  unidade: string;
  peso: number;
  qtdpec: number;
  metros?: number;
  compmin: number;
  compmax: number;
  codloc: number;
  autorizacaoEncarregado: string; // reunião 28/10/2026: emitida pelo Local de Estoque após validar o token do encarregado
  sucata: number;
  pontas: number;
  compminPontas: number;
  compmaxPontas: number;
  numlotOrigemPontas?: number; // reunião 28/10/2026: pontas voltam ao lote de origem
  aisiSucata?: number; // reunião 28/10/2026: liga do lote de sucata
  codlocSucata?: number;
}
// reunião 28/10/2026: opções de pontas (lote de origem) e sucata (lote por liga) — server/src/queries/sucata-pontas.ts
export interface SucataPontasOpcoes {
  ok: boolean; erro?: string;
  lotesOrigem: { numlot: number; nompro: string; unidade: string; kg: boolean; consumido: number; aisi: number }[];
  ligas: { codigo: number; nome: string }[];
  ligaSugerida: number;
  lotesSucata: { aisi: number; numlot: number; nomloc: string; saldo: number }[];
}
export interface EmpenhoMaterial {
  seqemp: number; numlot: number; nomloc: string; nommat: string; unidade: string; qtdemp: number; qtdfat: number; situac: string; datemp: string;
}
export interface EmpenhosMaterialResp {
  ok: boolean; erro?: string; numpcf: number; situacPcf: string; codpro: number; nomcli: string; nompro: string; bloqueada: boolean;
  itens: EmpenhoMaterial[]; totSel: number; totFat: number;
}
export interface LoteEmpenho {
  numlot: number; nommat: string; nomloc: string; comprimento: string; unidade: string; qtdLote: number; qtdUtilizada: number;
  qtdEmpenhada: number; qtdFaturada: number; saldo: number; erro?: string;
}
export interface PcfAConfirmar { numpcf: number; numped: number; nompro: string; tipo: string; datemi: string; sequen: number; codpro: number }
export interface PcfAFinalizar { posicao: number; numpcf: number; nomfan: string; situac: string; datent: string; etapa: string; datini: string }
export interface PecaFabricada { sequen: number; compri: number; qtdpec: number; numlot: number; peso: number; nomloc: string; codloc: number }
export interface PecasResp {
  ok: boolean; erro?: string; numpcf: number; finalizada: boolean;
  cab: { nomcli: string; tipo: string; numped: number; datemi: string; datent: string; nomusu: string; nomven: string; situac: string; nompro: string };
  pecas: PecaFabricada[]; locais: { codloc: number; nomloc: string }[];
  sucataPontas: { peso_pontas: number; codloc_pontas: number; numlot_pontas: number; peso_sucata: number; codloc_sucata: number; numlot_sucata: number; numlot_origem_pontas: number; aisi_sucata: number };
  opcoes: SucataPontasOpcoes;
}
type Ok = { ok: boolean; erro?: string; campo?: string };
export const apiPcfEstoque = {
  empenhos: (numpcf: number) => get<EmpenhosMaterialResp>(`/api/pcf-empmat/${numpcf}`),
  lote: (numpcf: number, numlot: number) => get<LoteEmpenho>(`/api/pcf-empmat/${numpcf}/lote/${numlot}`),
  registro: (numpcf: number, seqemp: number) =>
    get<{ ok: boolean; erro?: string; seqemp: number; lote: LoteEmpenho; qtdemp: number; situac: string; podeExcluir: boolean; podeAlterar: boolean }>(`/api/pcf-empmat/${numpcf}/registro/${seqemp}`),
  log: (numpcf: number) => get<{ numpcf: number; nomcli: string; truncado: boolean; linhas: { dataHora: string; historico: string; usuario: string }[] }>(`/api/pcf-empmat/${numpcf}/log`),
  incluir: (numpcf: number, numlot: number, qtd: number) => post<Ok>(`/api/pcf-empmat/${numpcf}/incluir`, { numlot, qtd }),
  alterar: (numpcf: number, seqemp: number, qtd: number) => post<Ok>(`/api/pcf-empmat/${numpcf}/alterar`, { seqemp, qtd }),
  excluir: (numpcf: number, seqemp: number) => post<Ok>(`/api/pcf-empmat/${numpcf}/excluir`, { seqemp }),
  confirmacao: () => get<{ linhas: PcfAConfirmar[]; truncado: boolean }>("/api/pcf-confirmacao"),
  liberar: (numpcf: number) => post<Ok>(`/api/pcf-confirmacao/${numpcf}/liberar`, {}),
  finalizacao: () => get<PcfAFinalizar[]>("/api/pcf-finalizacao"),
  consulta: (numpcf: number) => get<{ posicao: number; etapa: string; situac: string; inicio: string; fim: string }[]>(`/api/pcf-finalizacao/${numpcf}/consulta`),
  pecas: (numpcf: number) => get<PecasResp>(`/api/pcf-finalizacao/${numpcf}`),
  gravarPeca: (numpcf: number, p: { sequen?: number; compri: number; qtdpec: number; peso: number; codloc: number }) => post<Ok>(`/api/pcf-finalizacao/${numpcf}/peca`, p),
  excluirPeca: (numpcf: number, sequen: number) => post<Ok>(`/api/pcf-finalizacao/${numpcf}/peca/excluir`, { sequen }),
  gravarSucata: (numpcf: number, p: { pesoPontas: number; codlocPontas: number; pesoSucata: number; codlocSucata: number; numlotOrigemPontas: number; aisiSucata: number }) => post<Ok>(`/api/pcf-finalizacao/${numpcf}/sucata`, p),
  finalizar: (numpcf: number, parcial: boolean) => post<Ok & { lotes?: number[] }>(`/api/pcf-finalizacao/${numpcf}/finalizar`, { parcial }),
};
